/**
 * Global Request Manager
 * Cancels all pending requests when navigating between pages
 */

class RequestManager {
  constructor() {
    this.pendingRequests = new Map();
    this.requestId = 0;
  }

  // Generate unique request ID
  generateId() {
    return ++this.requestId;
  }

  // Register a new request with its abort controller
  register(controller, tag = 'default') {
    const id = this.generateId();
    this.pendingRequests.set(id, { controller, tag, timestamp: Date.now() });
    return id;
  }

  // Unregister a completed request
  unregister(id) {
    this.pendingRequests.delete(id);
  }

  // Cancel all pending requests (called on navigation)
  cancelAll() {
    this.pendingRequests.forEach(({ controller }, id) => {
      try {
        controller.abort();
      } catch (e) {
        // Ignore abort errors
      }
    });
    this.pendingRequests.clear();
  }

  // Cancel requests by tag (e.g., 'mcq-generation', 'concept-intelligence')
  cancelByTag(tag) {
    this.pendingRequests.forEach(({ controller, tag: reqTag }, id) => {
      if (reqTag === tag) {
        try {
          controller.abort();
        } catch (e) {
          // Ignore abort errors
        }
        this.pendingRequests.delete(id);
      }
    });
  }

  // Get count of pending requests
  getPendingCount() {
    return this.pendingRequests.size;
  }
}

// Singleton instance
export const requestManager = new RequestManager();

// Helper hook for components
export const createManagedRequest = (tag = 'default') => {
  const controller = new AbortController();
  const id = requestManager.register(controller, tag);
  
  return {
    signal: controller.signal,
    cancel: () => {
      controller.abort();
      requestManager.unregister(id);
    },
    cleanup: () => {
      requestManager.unregister(id);
    }
  };
};

export default requestManager;
